import { Account, AccountEvent } from '../types';
import { AccountFormData, BulkImportData } from '../schemas/account';
import { staffApi } from './mockRepo';

let accountDatabase: Account[] = [
  {
    id: 'account-1',
    staffId: 'staff-1',
    email: 'sarah.johnson@medsystem.com',
    role: 'doctor',
    status: 'active',
    lastLoginAt: new Date(Date.now() - 86400000).toISOString(),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'account-2',
    staffId: 'staff-2',
    email: 'maria.rodriguez@medsystem.com',
    role: 'nurse',
    status: 'active',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }
];

let eventDatabase: AccountEvent[] = [];
let nextAccountId = 3;
let nextEventId = 1;

export const accountService = {
  async list(filters?: {
    role?: string;
    status?: string;
    search?: string;
  }): Promise<Account[]> {
    await new Promise(resolve => setTimeout(resolve, 100));
    
    let filtered = [...accountDatabase];
    
    if (filters?.role) {
      filtered = filtered.filter(account => account.role === filters.role);
    }
    
    if (filters?.status) {
      filtered = filtered.filter(account => account.status === filters.status);
    }
    
    if (filters?.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(account => 
        account.email.toLowerCase().includes(searchLower)
      );
    }
    
    return filtered.sort((a, b) => a.email.localeCompare(b.email));
  },

  async get(id: string): Promise<Account | null> {
    await new Promise(resolve => setTimeout(resolve, 100));
    return accountDatabase.find(account => account.id === id) || null;
  },

  async create(data: AccountFormData & { method: "invite" | "password" }): Promise<{
    account: Account;
    tempPassword?: string;
    inviteToken?: string;
  }> {
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const existingAccount = accountDatabase.find(acc => acc.staffId === data.staffId);
    if (existingAccount) {
      throw new Error('Staff member already has an account');
    }
    
    const emailExists = accountDatabase.find(acc => acc.email === data.email);
    if (emailExists) {
      throw new Error('Email address is already in use');
    }
    
    const newAccount: Account = {
      id: `account-${nextAccountId++}`,
      staffId: data.staffId,
      email: data.email,
      phone: data.phone,
      role: data.role,
      status: data.method === "invite" ? "invited" : "active",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    
    accountDatabase.push(newAccount);
    
    await this.recordEvent({
      accountId: newAccount.id,
      action: "created",
      byUserId: "current-admin-id",
      details: `Account created via ${data.method}`,
    });
    
    const result: any = { account: newAccount };
    
    if (data.method === "invite") {
      result.inviteToken = `invite-${newAccount.id}-${Date.now()}`;
    } else {
      result.tempPassword = `temp-${Math.random().toString(36).slice(2, 10)}`;
    }
    
    return result;
  },

  async update(id: string, updates: Partial<Account>): Promise<Account> {
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const index = accountDatabase.findIndex(account => account.id === id);
    if (index === -1) {
      throw new Error('Account not found');
    }
    
    const updatedAccount = {
      ...accountDatabase[index],
      ...updates,
      updatedAt: new Date().toISOString(),
    };
    
    accountDatabase[index] = updatedAccount;
    return updatedAccount;
  },

  async setStatus(id: string, status: "active" | "disabled"): Promise<Account> {
    const account = await this.update(id, { status });
    
    await this.recordEvent({
      accountId: id,
      action: status === "active" ? "activated" : "disabled",
      byUserId: "current-admin-id",
      details: `Account ${status}`,
    });
    
    return account;
  },

  async resetPassword(id: string): Promise<{ tempPassword: string }> {
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const tempPassword = `temp-${Math.random().toString(36).slice(2, 10)}`;
    
    await this.recordEvent({
      accountId: id,
      action: "password_reset",
      byUserId: "current-admin-id",
      details: "Password reset by admin",
    });
    
    return { tempPassword };
  },

  async changeRole(id: string, role: string): Promise<Account> {
    const account = await this.update(id, { role: role as any });
    
    await this.recordEvent({
      accountId: id,
      action: "role_changed",
      byUserId: "current-admin-id",
      details: `Role changed to ${role}`,
    });
    
    return account;
  },

  async recordEvent(eventData: Omit<AccountEvent, 'id' | 'createdAt'>): Promise<AccountEvent> {
    await new Promise(resolve => setTimeout(resolve, 50));
    
    const event: AccountEvent = {
      ...eventData,
      id: `event-${nextEventId++}`,
      createdAt: new Date().toISOString(),
    };
    
    eventDatabase.push(event);
    return event;
  },

  async bulkImport(csvRows: BulkImportData[]): Promise<{
    preview: Array<{ empCode: string; email: string; role: string; status: 'valid' | 'error'; error?: string }>;
    commit?: () => Promise<Account[]>;
  }> {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const preview: Array<{ empCode: string; email: string; role: string; status: 'valid' | 'error'; error?: string }> = [];
    const validRows: Array<BulkImportData & { staffId: string }> = [];
    
    for (const row of csvRows) {
      try {
        const allStaff = await staffApi.getAll();
        const staff = allStaff.find(s => s.empCode === row.empCode);
        
        if (!staff) {
          preview.push({
            ...row,
            status: 'error' as const,
            error: 'Staff member not found'
          });
          continue;
        }
        
        const existingAccount = accountDatabase.find(acc => acc.staffId === staff.id);
        if (existingAccount) {
          preview.push({
            ...row,
            status: 'error' as const,
            error: 'Account already exists'
          });
          continue;
        }
        
        preview.push({ ...row, status: 'valid' as const });
        validRows.push({ ...row, staffId: staff.id });
      } catch (error) {
        preview.push({
          ...row,
          status: 'error' as const,
          error: 'Validation failed'
        });
      }
    }
    
    return {
      preview,
      commit: validRows.length > 0 ? async () => {
        const newAccounts = [];
        for (const row of validRows) {
          const account = await this.create({
            staffId: row.staffId,
            email: row.email,
            role: row.role as any,
            phone: row.phone,
            method: "invite"
          });
          newAccounts.push(account.account);
        }
        return newAccounts;
      } : undefined
    };
  },

  async getEvents(accountId: string): Promise<AccountEvent[]> {
    await new Promise(resolve => setTimeout(resolve, 100));
    return eventDatabase
      .filter(event => event.accountId === accountId)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }
};
